/**
 * Opening Stripe's hosted Customer Portal from the account menu (ELARA-1014).
 *
 * WEB ONLY, deliberately. The native Account screen shows the same membership card but carries no
 * portal row: pointing at subscription management from an iOS binary is what triggers Apple's
 * in-app-purchase requirement, which Elara does not want to take on. If you are here to add the row
 * to mobile, that is the reason it is missing.
 *
 * A hook rather than a component because the row it drives lives in the settings menu while the
 * membership summary lives in the other column — the action and the thing it acts on are rendered
 * apart, so the behaviour cannot sit inside either one.
 */

import type { MembershipSubscription } from '@elara/domain/subscription';

import { useState } from 'react';

import { planRequiresPayment } from '@elara/domain/membership-plans';

import { useMembershipPaymentState, useToastState } from '#states/hooks.js';

export interface BillingPortal {
  /**
   * Whether to offer the row at all.
   *
   * False for a member with no `subscription`: they have no `stripeCustomerId` either, so
   * `OpenBillingPortalFlow` answers them with `conflict` — a row that could only fail. Every member
   * predating the billing rollout is in that state, as is any environment without Stripe.
   *
   * Also false for a comped plan. It has no card, no invoices and nothing to renew, and the portal
   * would offer it a cancel button with no self-serve way back (see `membershipAccess`: a cancelled
   * comp lands in `ended`, which only a person can undo).
   */
  canManage: boolean;
  /** True from the click until the redirect, so the row can say it is working. */
  isOpening: boolean;
  openPortal: () => Promise<void>;
}

export function useBillingPortal(subscription: MembershipSubscription | undefined): BillingPortal {
  const membershipState = useMembershipPaymentState();
  const toastState = useToastState();
  const [isLeaving, setIsLeaving] = useState(false);

  const openPortal = async () => {
    setIsLeaving(true);
    const url = await membershipState.openBillingPortal();
    if (!url) {
      setIsLeaving(false);
      // The store records the failure on itself, but the member clicked a row in the menu and the
      // membership summary is in the other column — a toast is the only feedback that lands where
      // they are looking.
      toastState.addToast({
        title: membershipState.error ?? 'We could not open your billing settings. Please try again.',
        variant: 'destructive',
      });
      return;
    }
    // Same tab, always. A portal session is a short-lived credential and the member has to come back
    // through its return URL; `_blank` strands them in a second tab with a stale first one behind
    // it, which is the regression ELARA-851 fixed for bookings. Do not reintroduce it here.
    window.location.assign(url);
  };

  return {
    canManage: subscription !== undefined && planRequiresPayment(subscription.plan),
    isOpening: isLeaving || membershipState.isOpeningPortal,
    openPortal,
  };
}
